/**
 * 账户页 —— 「日常账户 + 社保账户（默认收起）」+ 🔴 **增 / 改 / 删闭环**（B 域 B1）
 *
 * ## 依据
 * - B 域 PRD **§5.4.1 / §5.5.1**（建账本即 12 个账户：3 日常 + 9 社保）· **§5.1.5**（字段表）；
 * - `AC-B6.7` 红线（**无「直接改账户余额」路径**）· `AC-B2.8`（额度未填不显示使用率）；
 * - 架构 **§4.2**（`category` / `contraKind` 互斥）· 设计 `14-资产_原型.html:405`（新建按钮）。
 *
 * ## 🔴 折叠是**端侧决定**，不是服务端过滤
 * `hidden` 不参与 `AccountService.list` 的过滤（`AccountService.list` 全量返回）⇒ 展开开关就是那个
 * 决定，状态在 `store.tsx`（跨页保持）。若服务端过滤，工资条录入时找不到落点。
 *
 * ## 🔴 余额一律用服务端下发的 `balance`
 * 它是**派生值**（`initialBalance + Σin − Σout`）⇒ 端侧**不重算**（重算 = 把口径抄第二遍）。
 *
 * ## 🔴 删除的两道闸（服务端 `remove()`，端侧**分别呈现**）
 *
 * | 闸 | 服务端响应 | 端侧呈现 |
 * |---|---|---|
 * | ① **被引用** | `409` + `details.references[]` | 逐条列出「流水 12 条、快照 3 条…」+ 原文建议「改为隐藏」⇒ **不给**确认按钮（点也没用） |
 * | ② **余额 ≠ 0** | `409` + `details.requiresConfirmation === true` | 展示「将清零 ¥X」+ 出现**第二次点击**的确认按钮 |
 *
 * 🔴 **判据用 `details.requiresConfirmation`（服务端显式旗标）而不是猜 `message` 文案** ——
 * 猜文案等于把服务端的排版当成接口（与 `apiMessages()` 同一条纪律）。
 *
 * ## 关于两步删除
 * 与 `PoliciesPage` 同范式：按钮变成「确认删除」而不是 `window.confirm()`
 * （① 不用浏览器弹窗，静态渲染下可断言；② 破坏性动作要求第二次点击）。
 * ⚠️ 但账户的"确认"多一层：**余额非零的确认是服务端要求的那一次**
 * （`confirmNonZeroBalance=true`），不是本地的第二次点击 —— 两者都要有。
 */

import { useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountType,
  centsToYuanText,
  formatMoneyText,
  isBalanceAdjustable,
  toCents,
  type AccountView,
  type Cents,
  type CreateAccountRequest,
} from '@jiaying/shared';
import { Loading } from '../ui/Loading.js';
import { Amount } from '../ui/Amount.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { AccountForm } from '../ui/AccountForm.js';
import { AccountLedgerDrawer } from '../ui/AccountLedgerDrawer.js';
import { AdjustBalanceDialog } from '../ui/AdjustBalanceDialog.js';
// 🆕 批次 97：信用卡账单区搬进本页（`DR-057` D2）
import { CreditCardSection } from '../ui/CreditCardSection.js';
import {
  createAccount,
  deleteAccount,
  listAccounts,
  updateAccount,
  useAccountPass,
} from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { accountTypeLabel } from '../features/account-labels.js';
import { apiMessages, deleteAdvice } from '../features/account-errors.js';
import { invalidateAfterTxnWrite } from '../features/invalidate.js';
import { useBaseCurrency } from '../app/useBaseCurrency.js';
import {
  buildCreateAccountRequest,
  buildUpdateAccountRequest,
  draftFromView,
  emptyAccountDraft,
  type AccountDraft,
} from '../features/account-draft.js';

/**
 * ⚠️ 错误文案映射已移到 `features/account-errors.ts`（批次 `94`）——
 * 债权债务页也要用同一份（否则同一个 409 在两个页面给出不同的下一步建议）。
 */

export function AccountsPage() {
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  /** `null` = 表单关闭；`'create'` = 新建；其它 = 正在编辑该 id */
  const [editing, setEditing] = useState<'create' | string | null>(null);
  const [draft, setDraft] = useState<AccountDraft>(() => emptyAccountDraft());
  const [errors, setErrors] = useState<readonly string[]>([]);
  /** 两步删除①：非空 = 正在等第二次点击 */
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  /**
   * 两步删除②：非空 = **服务端要求**的"我知道余额会被清零"确认
   * （`details.requiresConfirmation === true`，见 `features/account-errors.ts`）。
   * 存**整条原始消息**而不只是 id —— 里面含服务端算好的余额与币种，端侧不重算。
   */
  const [balanceWarning, setBalanceWarning] = useState<{ id: string; message: string } | null>(null);
  /**
   * 🔴 内容区引用 —— 交给 `<AnchorBar>` 做吸顶页内导航（`AC-O2.36`）。
   * 必须指向**内容区的直接容器**（本页 = 最外层 `<Stack>`），理由见 `AnchorBar.tsx` 使用纪律 §1。
   */
  const contentRef = useRef<HTMLDivElement | null>(null);
  /** 明细抽屉的目标账户（`null` = 关闭）—— 通用明细，见 `ui/AccountLedgerDrawer`
   *  🔴 批次 95 热修：这一行必须在下面的 `isPending/isError` 早退出**之前**
   *  （原来它排在守卫之后 ⇒ 首帧早退出少一个 hook ⇒ React #310「Rendered more hooks」→ 整页白屏）。 */
  const [ledgerId, setLedgerId] = useState<string | null>(null);
  /**
   * 🆕 批次 96：正在校准余额的账户 id（`null` = 对话框关闭）—— `DR-056`
   *
   * 🔴 **和 `ledgerId` 同理，必须排在下面的 `isPending/isError` 早退出之前**：
   * 排到守卫之后 ⇒ 首帧早退出少一个 hook ⇒ React #310「Rendered more hooks than
   * during the previous render」⇒ `/accounts` 整页白屏（批 95 就是这么白屏的，!127 修的）。
   */
  const [adjustId, setAdjustId] = useState<string | null>(null);
  const navigate = useNavigate();

  // ── 🔴 以下两个 hook 必须在 `isPending/isError` 早退出**之前**（React #310）──
  /**
   * 🆕 分组小计只累加**本币**账户（`DR-004`：不跨币种相加）。
   * `null` = 拿不到本币（未选中账本）⇒ 不显示小计，而不是显示一个混合币种的假数。
   */
  const baseCurrency = useBaseCurrency();

  /**
   * 🆕 预付卡「用一次」（`DR-057` D4.3）—— 扣一次单次金额 + 已用次数 +1。
   * 🔴 成功后用统一失效表（`invalidateAfterTxnWrite`）刷新余额/流水/净资产，
   *    否则会看到"次数变了、余额没变"。
   */
  const usePass = useMutation({
    mutationFn: (id: string) => useAccountPass(id),
    onSuccess: () => {
      invalidateAfterTxnWrite(qc);
      setErrors([]);
    },
    onError: (e) => setErrors(apiMessages(e)),
  });


  const close = (): void => {
    setEditing(null);
    setErrors([]);
    setConfirmingId(null);
    setBalanceWarning(null);
  };

  const save = useMutation({
    // 🔴 请求体在 `submit()` 里就已经铸好（本地校验通过才发）⇒ 这里只分派 create / update
    mutationFn: (req: CreateAccountRequest): Promise<AccountView> =>
      editing === null || editing === 'create'
        ? createAccount(req)
        : updateAccount(editing, req),
    onSuccess: () => {
      close();
      // 🔴 账户余额计入净资产 ⇒ 净资产缓存也必须失效（否则首页 Hero 是旧数）
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
    },
    onError: (e: unknown) => setErrors(apiMessages(e)),
  });

  const remove = useMutation({
    // 🔴 `confirmNonZeroBalance` 是**服务端要求的确认**：只有走过"余额非零 409"才置 true
    mutationFn: ({ id, confirmed }: { id: string; confirmed: boolean }) =>
      deleteAccount(id, confirmed),
    onSuccess: () => {
      close();
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
    },
    onError: (e: unknown, vars: { id: string; confirmed: boolean }) => {
      const details = e instanceof ApiError ? (e.body.details as { requiresConfirmation?: unknown } | undefined) : undefined;
      if (details?.requiresConfirmation === true) {
        // 余额非零 ⇒ 不再当"错误"渲染，而是升级成**待确认状态**（含服务端原文里的金额）
        setConfirmingId(null);
        setBalanceWarning({ id: vars.id, message: apiMessages(e)[0] ?? '该账户仍有余额' });
        setErrors([]);
        return;
      }
      const advice = deleteAdvice(e);
      setErrors(advice === null ? apiMessages(e) : [...apiMessages(e), advice]);
    },
  });

  const openCreate = (): void => {
    setDraft(emptyAccountDraft());
    setErrors([]);
    setConfirmingId(null);
    setBalanceWarning(null);
    setEditing('create');
  };

  const openEdit = (a: AccountView): void => {
    setDraft(draftFromView(a));
    setErrors([]);
    setConfirmingId(null);
    setBalanceWarning(null);
    setEditing(a.id);
  };

  const submit = (): void => {
    // 🔴 create 与 update 是**两个** build 函数（字段集不同：编辑不含 type/ownerId/initialBalance）
    const built =
      editing === 'create' ? buildCreateAccountRequest(draft) : buildUpdateAccountRequest(draft);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors([]);
    save.mutate(built.request as CreateAccountRequest);
  };

  const startDelete = (id: string): void => {
    setErrors([]);
    setBalanceWarning(null);
    remove.mutate({ id, confirmed: false });
  };

  // ── 守卫（**都在 hooks 之后** —— 顺序不可调换，否则 hook 数量会随分支变化）──
  if (accounts.isPending) return <Loading />;
  if (accounts.isError) return <Alert severity="error">账户读取失败：{String(accounts.error)}</Alert>;

  const all = accounts.data.items;
  const ledgerAccount = ledgerId === null ? null : (all.find((a) => a.id === ledgerId) ?? null);
  /**
   * 🆕 **账户页按 8 类分组**（批次 `95` · 负责人 2026-10-22：
   * 「新建一个账户页面，把所有账户按照分类排布，信用卡移到这里，在资产汇总」）。
   *
   * 🔴 这里是**管理视图**：五类账户（含信用卡 / 投资 / 内部挂账 / 往来）全部列出来，
   * 因为"改一张卡的账期"必须能在这张卡所在的地方做。
   * 🆕 批 `98`（`DR-058`）起，信用卡的**业务视图**（本期应还 / 未出账单 / 还款）也搬到本页，
   * 并进同一张卡面 —— 不再有"另一页看账单"这回事（批 `105` · K6 顺手订正本条注释：
   * 它原来还写着"业务视图仍在「债权债务」页"，正是那句错误文案的出处）。
   *
   * 🔴 `hidden` 的账户**照样列出**（标「已收起」）—— 收起只是"不进日常两段"，不是删除；
   * 管理页若也藏起来，用户就再也找不回来了。
   */
  const GROUPS: readonly { key: string; label: string; hint: string; types: readonly AccountType[] }[] = [
    {
      key: 'cash',
      label: '现金与储蓄',
      hint: '现金 / 储蓄卡 / 电子钱包 —— 日常收支的落点',
      types: [AccountType.Cash, AccountType.Debit, AccountType.Ewallet],
    },
    {
      key: 'credit',
      label: '信用卡',
      // 🔴 批 105 · K6：账单早在批 98 就搬到**这张卡上**了（`DR-058`），原文案还指着
      //    「债权债务」页 —— 点过去找不到，用户只会认为账单丢了。
      hint: '额度 / 已用 / 账期 —— 账单、往期与还款都在这张卡上',
      types: [AccountType.Credit],
    },
    {
      key: 'investment',
      label: '投资',
      hint: '未买入的现金（持仓与买卖在「投资」页）',
      types: [AccountType.Investment],
    },
    {
      key: 'internal',
      label: '内部挂账',
      hint: '预付 / 待摊 / 社保 —— 钱已付出或挂在对公机构上',
      types: [AccountType.Internal],
    },
    { key: 'loan', label: '贷款', hint: '贷款账户（负债）', types: [AccountType.Loan] },
    {
      key: 'contra',
      label: '债权债务',
      hint: '借出 / 借入 / 应收应付 / 垫付报销 —— 结算在「债权债务」页',
      types: [AccountType.Contra],
    },
  ];

  const groupAccounts = (types: readonly AccountType[]): AccountView[] =>
    all.filter((a) => (types as readonly string[]).includes(a.type));

  /**
   * 🆕 分组小计（`DR-057` D1.1）。
   *
   * 🔴 只累加**本币**账户；有外币账户时**如实告知**"另有 N 个不计入" ——
   * 把外币按 1:1 加进去（或偷偷跳过不说）都是同一个错的两种写法。
   */
  const subtotalOf = (items: readonly AccountView[]): { sum: number; fxCount: number } | null => {
    if (baseCurrency === null) return null;
    const local = items.filter((a) => a.currency === baseCurrency);
    if (local.length === 0) return null;
    return {
      sum: local.reduce((acc, a) => acc + (a.balance as number), 0),
      fxCount: items.length - local.length,
    };
  };

  /** 🆕 校准对话框的目标账户（同样的「先取对象再判空」纪律 —— 账户可能已被别人删掉） */
  const adjustAccount = adjustId === null ? null : (all.find((a) => a.id === adjustId) ?? null);
  const busy = save.isPending || remove.isPending;
  const editingView = editing !== null && editing !== 'create' ? all.find((a) => a.id === editing) : undefined;

  /**
   * 右侧「金额 + 操作按钮」—— 🔴 **必须放在正常文档流里，不能用 `secondaryAction`**。
   *
   * ## 为什么不用 MUI 的 `secondaryAction`（`2026-10-13` 真踩，账户页 + 保单页同时中招）
   *
   * MUI 的 `secondaryAction` 是**绝对定位**（`position: absolute; right: 0`），它靠给
   * `ListItem` 加 **固定 `padding-right: 48`** 来"避免碰撞"（源码原话：*Add some space to
   * avoid collision as ListItemSecondaryAction is absolutely positioned*）。
   *
   * 🔴 **48px 是个假设，不是保证**：本页放的是**两个中文按钮**（编辑 + 删除，
   * `Stack spacing={1}`），实测宽 **132px** —— 超出保护带 **84px** ⇒ 金额被压进按钮区间。
   *
   * 无头实测（`_archive/e2e-b70-accounts-overlap.mjs` · 视口 1024）：
   * | 量 | 值 |
   * |---|---|
   * | `ListItem` padding-right（MUI 给的保护） | **48px** |
   * | 按钮组实测宽 | **132px** |
   * | 按钮组区间 | `677 → 809` |
   * | 金额区间 | `730 → 777` ⇒ 🔴 **整个落在按钮区间内**，`按钮左 − 金额右 = −100` |
   *
   * ## 为什么修法不是"把 paddingRight 调大"
   * 因为按钮宽度是**内容决定**的（中文/英文、字体、按钮个数都会变）⇒ 任何写死的像素
   * 都只是把"48 不够"换成"某个数不够"。**根治 = 让按钮不再脱离文档流**：改成真正的
   * flex 行，宽度由内容撑开，金额与按钮**永远不会重叠**（布局约束而非数值巧合）。
   *
   * ## 实现约束
   * - 金额**不参与收缩**（`flexShrink: 0`）—— 否则窄屏下它会被按钮挤成两行；
   * - 操作区**不换行**（`flexWrap: 'nowrap'`）—— 两按钮必须同排；
   * - 间距取令牌 `sp-4`（16px），不写字面量（铁律 36：间距只从生成产物取值）。
   */
  const rowTail = (a: AccountView): ReactElement => (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{ ml: 1, flexShrink: 0, flexWrap: 'nowrap' }}
    >
      <Stack sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
        <Amount cents={a.balance as never} currency={a.currency} sx={{ flexShrink: 0 }} />
        {/* 🆕 预付卡次卡（`DR-057` D4）—— `pass === null` 即"没登记次数"，什么都不显示 */}
        {a.pass !== null && (
          <Typography
            variant="caption"
            color={a.pass.remaining === 0 ? 'text.disabled' : 'text.secondary'}
            data-testid={`account-pass-${a.id}`}
          >
            剩 {a.pass.remaining}/{a.pass.total} 次
          </Typography>
        )}
      </Stack>
      {confirmingId === a.id ? (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => startDelete(a.id)}
            data-testid={`account-delete-confirm-${a.id}`}
          >
            确认删除
          </Button>
          <Button size="small" onClick={() => setConfirmingId(null)} disabled={busy}>
            取消
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1}>
          {/* 🆕 批次 97：次卡「用一次」—— 只在登记了次数、且还有剩余时出现 */}
          {a.pass !== null && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => usePass.mutate(a.id)}
              disabled={a.pass.remaining === 0 || usePass.isPending || busy}
              data-testid={`account-use-pass-${a.id}`}
            >
              用一次
            </Button>
          )}
          {/* 🆕 批次 95：记一笔（预选本账户）—— 预付/待摊此前没有任何就近入口 */}
          <Button
            size="small"
            onClick={() => navigate(`/new?account=${encodeURIComponent(a.id)}`)}
            disabled={editing !== null || busy}
            data-testid={`account-txn-${a.id}`}
          >
            记一笔
          </Button>
          {/* 🆕 批次 95：通用明细（往来牌「明细」在债权债务页，那份带结算行） */}
          <Button
            size="small"
            onClick={() => setLedgerId(a.id)}
            disabled={busy}
            data-testid={`account-ledger-${a.id}`}
          >
            明细
          </Button>
          {/* 🆕 批次 96：校准余额 —— 🔴 只对 现金/储蓄/电子钱包 渲染（银 行为判据在共享内核） */}
          {isBalanceAdjustable(a.type) && (
            <Button
              size="small"
              onClick={() => setAdjustId(a.id)}
              disabled={editing !== null || busy}
              data-testid={`account-adjust-${a.id}`}
            >
              校准
            </Button>
          )}
          <Button
            size="small"
            onClick={() => openEdit(a)}
            disabled={editing !== null || busy}
            data-testid={`account-edit-${a.id}`}
          >
            编辑
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => setConfirmingId(a.id)}
            disabled={editing !== null || busy}
            data-testid={`account-delete-${a.id}`}
          >
            删除
          </Button>
        </Stack>
      )}
    </Stack>
  );

  /** 列表行（日常与社保共用一个渲染 —— 差别只在次要行内容） */
  const renderRow = (a: AccountView): ReactElement => (
    <ListItem key={a.id} divider disablePadding sx={{ px: 2, py: 1 }}>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <span>{a.name}</span>
            {/* 🔴 类型中文名走 `account-labels`（唯一源），不在页内维护第二张映射表 */}
            <Chip size="small" variant="outlined" label={accountTypeLabel(a.type)} />
            {/*
              🆕 **锁定基准**（`DR-056` 方式 2 · 批 `105` · K8）。
              🔴 为什么必须有：两种校准方式**唯一的区别**是"改锚点之前的交易会不会改变余额"，
              而这在界面上一点痕迹都没有 —— 用户选了方式 2 之后永远是"锁没锁上猜不到"。
              🔴 `locked` / `anchorAmount` 由服务端下发（同一概念与 `AdjustBalanceResult` 同名同义）。
            */}
            {a.locked && (
              <Chip
                size="small"
                variant="outlined"
                label={lockedChipLabel(a)}
                data-testid={`account-locked-${a.id}`}
              />
            )}
          </Stack>
        }
        // 🔴 品类中文名由服务端下发（`categoryLabel`）—— 端侧不维护品类→中文的映射表
        secondary={
          a.categoryLabel !== null
            ? `${a.ownerName} · ${a.currency} · ${a.categoryLabel}`
            : `${a.ownerName} · ${a.currency}`
        }
      />
      {/* 🔴 金额与按钮同在一个 flex 行 —— 见 `rowTail()` 上方长注释（重叠真因） */}
      {rowTail(a)}
    </ListItem>
  );

  return (
    <Stack spacing={2} ref={contentRef}>
      {/* 🔴 吸顶页内导航（`AC-O2.36`）—— 必须是内容区第一个子节点；短页自动不产出 */}
      <AnchorBar contentRef={contentRef} />

      {/* 🔴 服务端要求的"余额会清零"确认 —— 独立于表单，因为表单此时是关着的 */}
      {balanceWarning !== null && (
        <Alert
          severity="warning"
          data-testid="account-balance-warning"
          action={
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                color="error"
                variant="contained"
                disabled={busy}
                onClick={() => remove.mutate({ id: balanceWarning.id, confirmed: true })}
                data-testid="account-balance-confirm"
              >
                确认清零并删除
              </Button>
              <Button size="small" onClick={close} disabled={busy}>
                取消
              </Button>
            </Stack>
          }
        >
          {/* 原文含服务端算好的余额与币种 —— 端侧**不重算、不重新排版** */}
          {balanceWarning.message}
        </Alert>
      )}

      {/*
        🆕 批次 95：**按 8 类分组的全量账户列表**。

        🔴 分组只在这里定义一次（`GROUPS`），渲染遍历它 —— 加一类账户只改一处。
        🔴 空组**整组不渲染**（"信用卡（0）"这种空卡只是噪音）。
      */}
      {GROUPS.map((g) => {
        const items = groupAccounts(g.types);
        if (items.length === 0) return null;
        return (
          <Card key={g.key} data-testid={`account-group-${g.key}`} data-anchor={g.label}>
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {g.label}（{items.length}）
                    </Typography>
                    {/* 🆕 分组小计（`DR-057` D1.1）—— 负数即"这类欠着"（信用卡 / 贷款组） */}
                    {(() => {
                      const sub = subtotalOf(items);
                      if (sub === null) return null;
                      return (
                        /**
                         * 🔴 批 105 · K7：外层必须 `component="span"`。
                         * `Typography` 默认渲染 `<p>`，而里面的 `<Amount>` 也是 `Typography`（`<p>`）
                         * ⇒ React 每次渲染都报 `In HTML, <p> cannot be a descendant of <p>`
                         * （浏览器自行纠错，视觉上看不出来 —— 正因如此它挡在前面掩盖了别的真错误）。
                         *
                         * 🔴 同时**去掉 `signed`**：`signed` 只对负数有意义（负号由内核的 U+2212 给出），
                         * 而正数被加上 `+` 是噪音 —— 没人把"资产 ¥+5,885.00"读成"增加了 5,885"。
                         * ⚠️ 去掉后负数仍是「−920.00」，不是"-920.00"（符号单源 `nucleus/currency.ts`）。
                         */
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                          data-testid={`account-subtotal-${g.key}`}
                        >
                          合计{' '}
                          <Amount
                            cents={sub.sum as never}
                            currency={baseCurrency ?? 'CNY'}
                            sx={{ fontWeight: 600 }}
                          />
                          {sub.fxCount > 0 ? `（另有 ${String(sub.fxCount)} 个外币账户不计入）` : ''}
                        </Typography>
                      );
                    })()}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {g.hint}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={openCreate}
                  disabled={editing !== null}
                  sx={{ flexShrink: 0 }}
                  data-testid={`account-new-${g.key}`}
                >
                  新建账户
                </Button>
              </Stack>
            </CardContent>
            <Divider />
            {/*
              🔴 **信用卡组不画账户行**（`DR-058`）——
              合并前同一张卡出现两次：一次是账户行（余额 + 编辑/明细/删除），
              一次是账单卡（应还/账期/还款）。用户要的是"所有信用卡信息的集中地"，
              ⇒ 一张卡 = 一张卡面，账户操作并进卡面（`CreditCardSection` 的 `onEdit` 等回调）。
            */}
            {g.key === 'credit' ? (
              <CreditCardSection
                accounts={items}
                allAccounts={all}
                onNewTxn={(a) => {
                  navigate(`/new?account=${encodeURIComponent(a.id)}`);
                }}
                onEdit={openEdit}
                onDelete={(a) => {
                  startDelete(a.id);
                }}
                onOpenLedger={(a) => {
                  setLedgerId(a.id);
                }}
              />
            ) : (
              <List dense>{items.map(renderRow)}</List>
            )}
            {/*
              🆕 批 99 B2：**投资组不再列持仓**（`DR-059` D1 / 投资域 PRD §4.3）。

              🔴 原来的 `HoldingsList`（批 95 的只读列表）已删除，不是"改为在别处也用一份"：
              同一个持仓列表存在两处时，改一处漏一处（铁律 24）。
              账户页只回答"这个平台还有多少没买入的现金"，持仓与买卖全部在「投资」页。
            */}
            {g.key === 'investment' && (
              <Box sx={{ px: 2, pt: 0.5, pb: 1.5 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate('/investments')}
                  data-testid="account-go-invest"
                >
                  去投资 → 持仓 / 买卖 / 回报率
                </Button>
              </Box>
            )}
          </Card>
        );
      })}

      {/* 🔴 表单**内联**（不是 Dialog）—— 理由见 `AccountForm` 文件头。空态时给出记账口径说明 */}
      {editing !== null ? (
        <AccountForm
          draft={draft}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          errors={errors}
          busy={busy}
          mode={editing === 'create' ? 'create' : 'edit'}
          // 🔴 编辑态的三个只读值取自**服务端视图**（不是草稿）：
          //    它们不进 PATCH 请求体，草稿里也就没有它们的"权威值"
          readonlyView={
            editingView === undefined
              ? undefined
              : {
                  ownerName: editingView.ownerName,
                  // 初始余额走**内核定点格式化**（恒两位小数，不含币种符）——
                  // 标签已写「初始余额（¥）」，值里再带一次符号会重复；
                  // 🔴 更不能自己写 `/ 100`（ESLint 块 A 已把 `/`、`toFixed` 全项目封死）
                  initialBalanceText: centsToYuanText(editingView.initialBalance as Cents),
                }
          }
          onSubmit={submit}
          onCancel={close}
        />
      ) : (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              账户口径
            </Typography>
            <Typography variant="body2" color="text.secondary">
              余额是**派生值**（初始余额 + 所有笔），由服务端算好下发 —— 端侧不重算。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              🔴 <strong>没有「直接改余额」的入口</strong>：初始余额只在建账户时写一次，
              事后校准请记一笔「调整」。需要换账户形态（如储蓄卡 → 信用卡）请删除后重建。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              删除前请留意：被流水 / 保单 / 快照引用的账户删不掉（服务端会拦）；
              只是不想让它进净资产，用「编辑 → 收起」即可。
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* 🆕 批次 95：通用账户明细（预付/待摊/信用卡/储蓄卡都用它） */}
      <AccountLedgerDrawer
        accountId={ledgerId}
        accountName={ledgerAccount?.name ?? ''}
        currency={ledgerAccount?.currency ?? 'CNY'}
        onClose={() => setLedgerId(null)}
      />

      {/* 🆕 批次 96：校准余额（`DR-056` · 两种方式二选一） */}
      {adjustAccount !== null && (
        <AdjustBalanceDialog account={adjustAccount} onClose={() => setAdjustId(null)} />
      )}

      {/* 保存 / 删除失败在**表单关闭时**也要看得见 —— 故单独一处 */}
      {errors.length > 0 && editing === null && (
        <Alert severity="error" data-testid="account-page-errors">
          <Stack component="ul" sx={{ m: 0, pl: 2 }}>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

/**
 * 锁定态 chip 的文案（`DR-056` 方式 2 · 批 `105` · K8）。
 *
 * 🔴 为什么连**基准金额**一起显示：只写「已锁定」等于只说"有这件事"，用户仍不知道
 * 余额从哪个数起算 —— 而"从 600 起算"与"从 800 起算"正是他需要核对的那件事。
 * 🔴 `anchorAmount === null` 时**不编一个 0 出来**（`0` 是合法的锚点余额，两种缺失不能混 —
 * 铁律 107），退化成只显示「已锁定」。
 */
function lockedChipLabel(a: AccountView): string {
  if (a.anchorAmount === null) return '已锁定';
  return `已锁定 · 基准 ${formatMoneyText(toCents(a.anchorAmount), a.currency)}`;
}
